/** Matches engine-service `DealTypeMPC` enum values */
export const AZURE_DEAL_TYPE = {
  IP_CO_SELL: 'IP Co-sell',
  PRIVATE: 'Private',
  PARTNER_LED: 'Partner-led',
  SERVICES_CO_SELL: 'Services Co-sell',
} as const

export type DealType = (typeof AZURE_DEAL_TYPE)[keyof typeof AZURE_DEAL_TYPE]

export const AZURE_DEAL_TYPE_OPTIONS: readonly DealType[] = [
  AZURE_DEAL_TYPE.IP_CO_SELL,
  AZURE_DEAL_TYPE.SERVICES_CO_SELL,
  AZURE_DEAL_TYPE.PRIVATE,
  AZURE_DEAL_TYPE.PARTNER_LED,
]

export const SHARED_AZURE_DEAL_TYPES: readonly DealType[] = [
  AZURE_DEAL_TYPE.IP_CO_SELL,
  AZURE_DEAL_TYPE.SERVICES_CO_SELL,
]

/** Matches engine-service `ReferralTypeMPC` enum values */
export const AZURE_MPC_REFERRAL_TYPE = {
  INDEPENDENT: 'Independent',
  SHARED: 'Shared',
} as const

export type AzureMpcReferralType =
  (typeof AZURE_MPC_REFERRAL_TYPE)[keyof typeof AZURE_MPC_REFERRAL_TYPE]

/** Matches engine-service `ReferralStatusType` enum values */
export const AZURE_REFERRAL_STATUS = {
  NEW: 'New',
  ACTIVE: 'Active',
  CLOSED: 'Closed',
} as const

export type ReferralStatus =
  (typeof AZURE_REFERRAL_STATUS)[keyof typeof AZURE_REFERRAL_STATUS]

/** Matches engine-service `ReferralSubStatusType` enum values */
export const AZURE_REFERRAL_SUBSTATUS = {
  PENDING: 'Pending',
  RECEIVED: 'Received',
  ACCEPTED: 'Accepted',
  WON: 'Won',
  LOST: 'Lost',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
} as const

export type ReferralSubstatus =
  (typeof AZURE_REFERRAL_SUBSTATUS)[keyof typeof AZURE_REFERRAL_SUBSTATUS]

export const TERMINAL_AZURE_REFERRAL_SUBSTATUSES: readonly ReferralSubstatus[] =
  [
    AZURE_REFERRAL_SUBSTATUS.WON,
    AZURE_REFERRAL_SUBSTATUS.LOST,
    AZURE_REFERRAL_SUBSTATUS.DECLINED,
    AZURE_REFERRAL_SUBSTATUS.EXPIRED,
  ]

/** Matches engine-service `ReferralSalesStage` enum values (MPC string format) */
export const AZURE_REFERRAL_SALES_STAGE = {
  STAGE_10: '10',
  STAGE_20: '20',
  STAGE_40: '40',
  STAGE_60: '60',
  STAGE_80: '80',
  STAGE_100: '100',
} as const

export type ReferralSalesStage =
  (typeof AZURE_REFERRAL_SALES_STAGE)[keyof typeof AZURE_REFERRAL_SALES_STAGE]

/** Numeric sales stage used in form state before API stringify */
export const AZURE_REFERRAL_NEW_SALES_STAGE = Number(
  AZURE_REFERRAL_SALES_STAGE.STAGE_10
)

/** Default status/substatus for newly created referrals */
export const AZURE_REFERRAL_CREATE_STATUS = AZURE_REFERRAL_STATUS.NEW
export const AZURE_REFERRAL_CREATE_SUBSTATUS = AZURE_REFERRAL_SUBSTATUS.PENDING

/** Matches engine-service `FormStatus` enum values */
export const AZURE_FORM_STATUS = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
} as const

export type AzureFormStatus =
  (typeof AZURE_FORM_STATUS)[keyof typeof AZURE_FORM_STATUS]

/** Matches engine-service `SolutionType` enum values (internal/DB) */
export const AZURE_SOLUTION_TYPE = {
  COMPANY_SOLUTION: 'COMPANY_SOLUTION',
  MICROSOFT_SOLUTION: 'MICROSOFT_SOLUTION',
  OTHER_THIRD_PARTY_SOLUTION: 'OTHER_THIRD_PARTY_SOLUTION',
} as const

export type AzureSolutionType =
  (typeof AZURE_SOLUTION_TYPE)[keyof typeof AZURE_SOLUTION_TYPE]

/** MPC API payload labels for solution types (distinct from internal SolutionType) */
export const AZURE_SOLUTION_API_TYPE = {
  COMPANY: 'Company',
  MICROSOFT: 'Microsoft',
  THIRD_PARTY: 'ThirdParty',
} as const

export type AzureSolutionApiType =
  (typeof AZURE_SOLUTION_API_TYPE)[keyof typeof AZURE_SOLUTION_API_TYPE]

export const AZURE_SOLUTION_TYPE_TO_API: Record<
  AzureSolutionType,
  AzureSolutionApiType
> = {
  [AZURE_SOLUTION_TYPE.COMPANY_SOLUTION]: AZURE_SOLUTION_API_TYPE.COMPANY,
  [AZURE_SOLUTION_TYPE.MICROSOFT_SOLUTION]: AZURE_SOLUTION_API_TYPE.MICROSOFT,
  [AZURE_SOLUTION_TYPE.OTHER_THIRD_PARTY_SOLUTION]:
    AZURE_SOLUTION_API_TYPE.THIRD_PARTY,
}

export const AZURE_SOLUTION_API_TO_TYPE: Record<string, AzureSolutionType> = {
  [AZURE_SOLUTION_API_TYPE.COMPANY]: AZURE_SOLUTION_TYPE.COMPANY_SOLUTION,
  [AZURE_SOLUTION_API_TYPE.MICROSOFT]: AZURE_SOLUTION_TYPE.MICROSOFT_SOLUTION,
  [AZURE_SOLUTION_API_TYPE.THIRD_PARTY]:
    AZURE_SOLUTION_TYPE.OTHER_THIRD_PARTY_SOLUTION,
  Cloud: AZURE_SOLUTION_TYPE.MICROSOFT_SOLUTION,
}

export const isAzureDealType = (dealType?: string): dealType is DealType =>
  dealType !== undefined &&
  (AZURE_DEAL_TYPE_OPTIONS as readonly string[]).includes(dealType)

export const isAzureReferralStatus = (
  status?: string
): status is ReferralStatus =>
  status !== undefined &&
  (Object.values(AZURE_REFERRAL_STATUS) as readonly string[]).includes(status)

export const isAzureReferralSubstatus = (
  substatus?: string
): substatus is ReferralSubstatus =>
  substatus !== undefined &&
  (Object.values(AZURE_REFERRAL_SUBSTATUS) as readonly string[]).includes(
    substatus
  )

export const isAzureFormStatus = (value?: string): value is AzureFormStatus =>
  value !== undefined &&
  (Object.values(AZURE_FORM_STATUS) as readonly string[]).includes(value)

export const isAzureSolutionType = (
  value?: string
): value is AzureSolutionType =>
  value !== undefined &&
  (Object.values(AZURE_SOLUTION_TYPE) as readonly string[]).includes(value)

export const matchesReferralStatus = (
  status: string | undefined,
  expected: ReferralStatus
): boolean => status?.toLowerCase() === expected.toLowerCase()

export const matchesReferralSubstatus = (
  substatus: string | undefined,
  expected: ReferralSubstatus
): boolean => substatus?.toLowerCase() === expected.toLowerCase()

export const isTerminalReferralSubstatus = (substatus?: string): boolean =>
  TERMINAL_AZURE_REFERRAL_SUBSTATUSES.some(terminal =>
    matchesReferralSubstatus(substatus, terminal)
  )

export const toReferralSalesStageString = (
  stage?: number | string
): ReferralSalesStage | undefined => {
  if (stage === undefined) return undefined
  const normalized = String(stage)
  return (Object.values(AZURE_REFERRAL_SALES_STAGE) as readonly string[]).includes(
    normalized
  )
    ? (normalized as ReferralSalesStage)
    : undefined
}
